/**
 * Color utility functions
 */

/**
 * Convert hex color to rgba string
 * @param hex - Hex color string (e.g., "#FF6347")
 * @param alpha - Alpha value (0-1)
 * @returns rgba string (e.g., "rgba(255,99,71,0.5)")
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/**
 * Convert hex to RGB
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

/**
 * Convert RGB to HSL
 */
export const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Convert HSL to RGB
 */
export const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
};

/**
 * Convert RGB to hex
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${[r, g, b].map((x) => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("")}`;
};

/**
 * Generate a muted version of a color by reducing saturation and adjusting lightness
 */
export const generateMutedColor = (hex: string): string => {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  
  // Reduce saturation by 30-40% and slightly adjust lightness
  const mutedS = Math.max(20, hsl.s * 0.6);
  const mutedL = hsl.l > 50 ? Math.min(70, hsl.l + 5) : Math.max(30, hsl.l - 5);
  
  const mutedRgb = hslToRgb(hsl.h, mutedS, mutedL);
  return rgbToHex(mutedRgb.r, mutedRgb.g, mutedRgb.b);
};

/**
 * Calculate relative luminance for a color (WCAG standard)
 * @param r - Red component (0-255)
 * @param g - Green component (0-255)
 * @param b - Blue component (0-255)
 * @returns Relative luminance (0-1)
 */
export const getRelativeLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculate contrast ratio between two colors (WCAG standard)
 * @param color1 - First color in hex format
 * @param color2 - Second color in hex format
 * @returns Contrast ratio (1-21)
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  const l1 = getRelativeLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = getRelativeLuminance(rgb2.r, rgb2.g, rgb2.b);
  
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Validate custom color for accessibility
 * @param hex - Color in hex format
 * @param backgroundHex - Background color in hex format (default: white/black based on mode)
 * @returns Validation result with warnings
 */
export interface ColorValidationResult {
  isValid: boolean;
  warnings: string[];
  contrastRatio: number;
  isTooLight: boolean;
  isTooDark: boolean;
}

export const validateCustomColor = (
  hex: string,
  backgroundHex: string = "#FFFFFF"
): ColorValidationResult => {
  const warnings: string[] = [];
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  
  // Check contrast ratio (WCAG AA requires 4.5:1 for normal text, 3:1 for large text)
  const contrastRatio = getContrastRatio(hex, backgroundHex);
  if (contrastRatio < 3) {
    warnings.push("Very low contrast - may be difficult to read");
  } else if (contrastRatio < 4.5) {
    warnings.push("Low contrast - may not meet accessibility standards");
  }
  
  // Check if color is too light (lightness > 90%)
  const isTooLight = hsl.l > 90;
  if (isTooLight) {
    warnings.push("Color is very light - may not be visible on light backgrounds");
  }
  
  // Check if color is too dark (lightness < 10%)
  const isTooDark = hsl.l < 10;
  if (isTooDark) {
    warnings.push("Color is very dark - may not be visible on dark backgrounds");
  }
  
  // Check if color is too desaturated (saturation < 20%)
  if (hsl.s < 20) {
    warnings.push("Color is very desaturated - may appear gray");
  }
  
  return {
    isValid: warnings.length === 0,
    warnings,
    contrastRatio,
    isTooLight,
    isTooDark,
  };
};

